import { useId, type SVGProps } from 'react';

type RailIconProps = SVGProps<SVGSVGElement>;

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/** Figma rail home icon (9062:223136) — 24×24. */
export function SidebarHomeNavIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: RailIconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      data-testid="sidebar-home-nav-icon"
      {...props}
    >
      <path
        d="M17 20.9985H7C4.79086 20.9985 3 19.2076 3 16.9985V10.7061C3 9.30737 3.73061 8.01025 4.92679 7.28529L9.92679 4.25499C11.2011 3.48271 12.7989 3.48271 14.0732 4.25499L19.0732 7.28529C20.2694 8.01025 21 9.30737 21 10.7061V16.9985C21 19.2076 19.2091 20.9985 17 20.9985Z"
        {...strokeProps}
      />
      <path d="M9 17H15" {...strokeProps} />
    </svg>
  );
}

/** Figma rail agents icon (9062:223141) — 22×22 robot with sparkle. */
export function SidebarAgentsNavIcon({
  width = 22,
  height = 22,
  className,
  ...props
}: RailIconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      data-testid="sidebar-agents-nav-icon"
      {...props}
    >
      <path
        d="M15.9964 3.78125H11.8594V2.54375C11.8594 2.41611 11.8087 2.29371 11.7184 2.20345C11.6282 2.1132 11.5058 2.0625 11.3781 2.0625H10.6219C10.4942 2.0625 10.3718 2.1132 10.2816 2.20345C10.1913 2.29371 10.1406 2.41611 10.1406 2.54375V3.78125H6.00359C5.14083 3.78171 4.31353 4.12464 3.70346 4.73471C3.09339 5.34478 2.75046 6.17208 2.75 7.03484V8.97359C2.75 11.1065 3.59728 13.152 5.10546 14.6602C6.61363 16.1683 8.65915 17.0156 10.792 17.0156H11.3781C11.5058 17.0156 11.6282 16.9649 11.7184 16.8747C11.8087 16.7844 11.8594 16.662 11.8594 16.5344V15.7781C11.8594 15.6505 11.8087 15.5281 11.7184 15.4378C11.6282 15.3476 11.5058 15.2969 11.3781 15.2969H10.792C9.11499 15.2969 7.50664 14.6307 6.3208 13.4448C5.13495 12.259 4.46875 10.6506 4.46875 8.97359V7.03484C4.46875 6.62778 4.63046 6.23738 4.9183 5.94955C5.20613 5.66171 5.59653 5.5 6.00359 5.5H15.9964C16.4035 5.5 16.7939 5.66171 17.0817 5.94955C17.3695 6.23738 17.5312 6.62778 17.5312 7.03484V9.10938C17.5302 9.58093 17.4725 10.0506 17.3594 10.5084C17.3294 10.6264 17.3454 10.7513 17.4041 10.8579C17.4628 10.9645 17.5599 11.0448 17.6756 11.0825L18.3975 11.318C18.4599 11.3387 18.5258 11.3463 18.5912 11.3405C18.6567 11.3346 18.7202 11.3154 18.7779 11.284C18.8356 11.2525 18.8862 11.2096 18.9266 11.1578C18.967 11.106 18.9964 11.0464 19.0128 10.9828C19.1691 10.3706 19.2488 9.74126 19.25 9.10938V7.03484C19.2495 6.17208 18.9066 5.34478 18.2965 4.73471C17.6865 4.12464 16.8592 3.78171 15.9964 3.78125Z"
        fill="currentColor"
      />
      <path
        d="M8.25 11C9.00939 11 9.625 10.3844 9.625 9.625C9.625 8.86561 9.00939 8.25 8.25 8.25C7.49061 8.25 6.875 8.86561 6.875 9.625C6.875 10.3844 7.49061 11 8.25 11Z"
        fill="currentColor"
      />
      <path
        d="M13.75 11C14.5094 11 15.125 10.3844 15.125 9.625C15.125 8.86561 14.5094 8.25 13.75 8.25C12.9906 8.25 12.375 8.86561 12.375 9.625C12.375 10.3844 12.9906 11 13.75 11Z"
        fill="currentColor"
      />
      <path
        d="M19.2036 15.95L20.7608 16.5636C20.7928 16.5763 20.8203 16.5983 20.8397 16.6268C20.8591 16.6553 20.8695 16.689 20.8695 16.7234C20.8695 16.7579 20.8591 16.7916 20.8397 16.8201C20.8203 16.8486 20.7928 16.8706 20.7608 16.8833L19.2036 17.4969C18.9279 17.6059 18.6776 17.7702 18.468 17.9798C18.2584 18.1894 18.094 18.4398 17.985 18.7155L17.3714 20.2623C17.3587 20.2944 17.3367 20.3219 17.3082 20.3413C17.2797 20.3607 17.246 20.371 17.2116 20.371C17.1771 20.371 17.1434 20.3607 17.1149 20.3413C17.0864 20.3219 17.0644 20.2944 17.0517 20.2623L16.4381 18.7155C16.3294 18.4397 16.1651 18.1892 15.9555 17.9795C15.7458 17.7699 15.4953 17.6056 15.2195 17.4969L13.6727 16.8833C13.6406 16.8706 13.6131 16.8486 13.5937 16.8201C13.5743 16.7916 13.564 16.7579 13.564 16.7234C13.564 16.689 13.5743 16.6553 13.5937 16.6268C13.6131 16.5983 13.6406 16.5763 13.6727 16.5636L15.2195 15.95C15.4953 15.8413 15.7458 15.677 15.9555 15.4674C16.1651 15.2577 16.3294 15.0072 16.4381 14.7314L17.0517 13.1845C17.0644 13.1525 17.0864 13.125 17.1149 13.1056C17.1434 13.0862 17.1771 13.0758 17.2116 13.0758C17.246 13.0758 17.2797 13.0862 17.3082 13.1056C17.3367 13.125 17.3587 13.1525 17.3714 13.1845L17.985 14.7314C18.094 15.0071 18.2584 15.2574 18.468 15.467C18.6776 15.6766 18.9279 15.841 19.2036 15.95Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Figma rail projects icon (9764:13569) — 20×20 open folder. */
export function SidebarProjectsNavIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: RailIconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      data-testid="sidebar-projects-nav-icon"
      {...props}
    >
      <path
        d="M8.34347 17.5H11.6562C14.1721 17.5 15.4301 17.5 16.3299 16.8629C16.3913 16.8194 16.451 16.774 16.5089 16.7267C17.3575 16.0337 17.5826 14.9054 18.0326 12.649C18.3457 11.0797 18.5022 10.295 18.0575 9.76325C18.0278 9.7278 17.9965 9.69355 17.9635 9.66062C17.4691 9.16667 16.5942 9.16667 14.8444 9.16667H5.15525C3.40545 9.16667 2.53055 9.16667 2.03618 9.66062C2.00321 9.69355 1.97186 9.7278 1.94221 9.76325C1.49752 10.295 1.65402 11.0797 1.96704 12.649C2.4171 14.9054 2.64213 16.0337 3.49078 16.7267C3.54867 16.774 3.60835 16.8194 3.66972 16.8629C4.56956 17.5 5.82753 17.5 8.34347 17.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      />
      <path
        d="M3.33317 9.16667V4.58333C3.33317 3.43274 4.26591 2.5 5.4165 2.5H7.44119C7.99846 2.5 8.51883 2.7785 8.82794 3.24217L9.99984 5M9.99984 5H7.08317M9.99984 5H14.5832C15.7338 5 16.6665 5.93274 16.6665 7.08333V9.16667"
        {...strokeProps}
      />
    </svg>
  );
}

/** Figma rail configure icon (9062:223153) — 20×20 gear. */
export function SidebarSettingsNavIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: RailIconProps) {
  const clipId = useId();

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
      data-testid="sidebar-settings-nav-icon"
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61925 11.3807 7.5 10 7.5C8.61925 7.5 7.5 8.61925 7.5 10C7.5 11.3807 8.61925 12.5 10 12.5Z"
          {...strokeProps}
        />
        <path
          d="M16.352 8.66283L15.4372 6.454L16.6667 5L15 3.33333L13.5539 4.56912L11.2982 3.64145L10.7794 1.66667H9.15083L8.62425 3.66761L6.42034 4.59663L5 3.33333L3.33333 5L4.54447 6.49071L3.64375 8.70525L1.66667 9.16667V10.8333L3.66759 11.3796L4.59646 13.5831L3.33333 15L5 16.6667L6.49263 15.4502L8.66417 16.3436L9.16667 18.3333H10.8333L11.3371 16.3443L13.5459 15.4296C13.9141 15.6927 15 16.6667 15 16.6667L16.6667 15L15.4299 13.5412L16.3449 11.3317L18.3332 10.8143L18.3333 9.16667L16.352 8.66283Z"
          {...strokeProps}
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
